// The mixing graph (decision 0045; audio card 6 and its swinging-hero variant): the standard buses, each with the
// player's volume (perceptual, v^1.5) and a duck gain, into a master with a glue compressor, a limiter and a soft
// ceiling, so the output never passes 0 dBFS. Dialogue-first ducking: under a line, music dips to −6.9 dB, effects to
// −3.2 dB and the beds to −5 dB (the game passes the levels), with a fast attack and a slow release. The music bus has
// layers (base, combat, sting) the director fades, and (the music pass) a space: a generated room on a convolver and
// a tempo-synced echo, fed by each layer's sends. Works on any BaseAudioContext, so an OfflineAudioContext renders the
// same graph for numeric tests.
import { dbToGain } from "./dsp";
import type { MusicLayer } from "./music";

/** The buses. */
export type BusId = "music" | "effects" | "ambience" | "voice" | "ui";
/** Every bus, in order. */
export const BUSES: readonly BusId[] = ["music", "effects", "ambience", "voice", "ui"];

/** Ducking under a line: dB per ducked bus, attack and release time constants (s). */
export interface DuckLevels {
  music: number;
  effects: number;
  ambience: number;
  attack: number;
  release: number;
}

/** The master's numbers. */
export interface MasterOptions {
  /** The glue compressor: threshold (dB), ratio, knee (dB), attack and release (s). */
  glue: { threshold: number; ratio: number; knee: number; attack: number; release: number };
  /** The limiter's threshold (dB); ratio 20, fast attack. */
  limit: number;
  /** The soft ceiling the output saturates under (linear; under 1 = under 0 dBFS). */
  ceiling: number;
}

/** Planted defects beside what they break: the limiter spec and the ducking spec must go red with them on. */
export const MIXER_FAULTS = {
  /** The master skips its compressor, limiter and ceiling: a pile of loud cues clips past 0 dBFS. */
  limiterBypassed: false,
  /** Ducking does nothing: music stays at full level under a line. */
  noDucking: false,
  /** The music's sends go nowhere: no reverb and no echo, the music dry (the reverb-tail check goes red). */
  noReverb: false,
};

/** The music's space (the music pass): a convolver (a generated room, card 4) and a tempo-synced echo, both returning
 *  to the music bus (so the player's music volume and ducking apply to them). Each note sends to them at its
 *  instrument's levels (the engine's `Instrument.reverb`, `.echo`) through its layer's send, which fades with the layer. */
export interface MusicSpace {
  /** The reverb's return level (dB), and the send of an instrument that names none (linear). */
  reverb: { wet: number; send: number };
  /** The echo: its time in beats (a dotted eighth is 0.75), feedback (0..0.9), a low-pass in its loop (Hz), its return
   *  level (dB), and how much of it feeds the reverb (linear). */
  delay?: { beats: number; feedback: number; lowpass: number; wet: number; toReverb?: number };
}

/** The soft-ceiling curve: linear to `knee`, then a tanh shoulder that never reaches `ceiling`. */
export function ceilingCurve(ceiling: number, n = 2049): Float32Array {
  const knee = ceiling * 0.82;
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const a = Math.abs(x);
    const y = a <= knee ? a : knee + (ceiling - knee) * Math.tanh((a - knee) / (ceiling - knee));
    c[i] = Math.sign(x) * y;
  }
  return c;
}

/** Set a param towards `target` from `at` with time constant `tau`, without jumps (cancel, hold, then ramp). */
export function rampTo(p: AudioParam, target: number, at: number, tau: number): void {
  p.cancelScheduledValues(at);
  p.setValueAtTime(p.value, at);
  p.setTargetAtTime(target, at, Math.max(0.001, tau));
}

/** The graph. */
export class Mixer {
  readonly input: Record<BusId, GainNode>;
  private readonly duckGain: Record<BusId, GainNode>;
  /** The music bus's layers (the director fades them). */
  readonly layers: Record<MusicLayer, GainNode>;
  readonly master: GainNode;
  /** The last node (connected to the destination). */
  readonly output: AudioNode;
  private duckUntil = -Infinity;
  /** Lines that ducked, for the handle. */
  ducks = 0;

  constructor(
    readonly ctx: BaseAudioContext,
    private readonly duck: DuckLevels,
    master: MasterOptions,
    destination: AudioNode = ctx.destination,
  ) {
    const g = (v = 1): GainNode => {
      const n = ctx.createGain();
      n.gain.value = v;
      return n;
    };
    this.master = g();
    this.input = { music: g(), effects: g(), ambience: g(), voice: g(), ui: g() };
    this.duckGain = { music: g(), effects: g(), ambience: g(), voice: g(), ui: g() };
    for (const b of BUSES) this.input[b].connect(this.duckGain[b]).connect(this.master);
    this.layers = { base: g(), combat: g(0), sting: g() };
    for (const l of Object.values(this.layers)) l.connect(this.input.music);
    this.sends = { reverb: { base: g(), combat: g(0), sting: g() }, echo: { base: g(), combat: g(0), sting: g() } };
    if (MIXER_FAULTS.limiterBypassed) {
      this.master.connect(destination);
      this.output = this.master;
      return;
    }
    const glue = ctx.createDynamicsCompressor();
    glue.threshold.value = master.glue.threshold;
    glue.ratio.value = master.glue.ratio;
    glue.knee.value = master.glue.knee;
    glue.attack.value = master.glue.attack;
    glue.release.value = master.glue.release;
    const lim = ctx.createDynamicsCompressor();
    lim.threshold.value = master.limit;
    lim.knee.value = 0;
    lim.ratio.value = 20;
    lim.attack.value = 0.001;
    lim.release.value = 0.1;
    const shaper = ctx.createWaveShaper();
    shaper.curve = ceilingCurve(master.ceiling) as Float32Array<ArrayBuffer>;
    // no oversampling: its resampling filter rings past the curve (a peak of 1.02 measured with "2x")
    shaper.oversample = "none";
    this.master.connect(glue).connect(lim).connect(shaper).connect(destination);
    this.output = shaper;
  }

  /** The player's volume for a bus (or the master), 0..1, perceptual. */
  volume(bus: BusId | "master", v: number): void {
    const gain = Math.pow(Math.min(1, Math.max(0, v)), 1.5);
    const p = bus === "master" ? this.master.gain : this.input[bus].gain;
    rampTo(p, gain, this.ctx.currentTime, 0.03);
  }

  /** Duck under a line from `start` to `end` (context seconds). Overlapping lines extend the duck. */
  duckFor(start: number, end: number): void {
    this.ducks++;
    if (MIXER_FAULTS.noDucking) return;
    this.duckUntil = Math.max(this.duckUntil, end);
    for (const b of ["music", "effects", "ambience"] as const) {
      const p = this.duckGain[b].gain;
      // a target curve starts from the value the automation has at `start` (a release still running included)
      p.cancelScheduledValues(start);
      p.setTargetAtTime(dbToGain(this.duck[b]), start, this.duck.attack);
      p.setTargetAtTime(1, this.duckUntil, this.duck.release);
    }
  }

  /** A bus's duck gain now (for the handle). */
  duckLevel(bus: BusId): number {
    return this.duckGain[bus].gain.value;
  }

  /** Fade a music layer (and its sends) from its value at `start` to `target` over `seconds` (a linear ramp, ending
   *  on the beat). */
  fadeLayer(layer: MusicLayer, target: number, start: number, seconds: number): void {
    for (const p of [this.layers[layer].gain, this.sends.reverb[layer].gain, this.sends.echo[layer].gain]) {
      p.cancelScheduledValues(start);
      // the value the last fade left (fades are scheduled ahead, so the param's value now is not the one at `start`)
      p.setValueAtTime(this.layerLevel[layer], start);
      p.linearRampToValueAtTime(target, start + Math.max(0.005, seconds));
    }
    this.layerLevel[layer] = target;
  }
  private readonly layerLevel: Record<MusicLayer, number> = { base: 1, combat: 0, sting: 1 };

  private echo: { node: DelayNode; beats: number; time: number } | null = null;
  /** The music's space, once built: the convolver and the echo (for the handle and the tests). */
  space: { convolver: ConvolverNode; delay: DelayNode | null; send: number } | null = null;

  /** Each music layer's sends into the space (a note connects to them at its instrument's levels); they fade with the
   *  layer, and go nowhere until `musicSpace` builds the room. */
  readonly sends: { reverb: Record<MusicLayer, GainNode>; echo: Record<MusicLayer, GainNode> };

  /** Build the music's space: `ir` the room's two channels at the context's rate. Once; a second call does nothing. */
  musicSpace(spec: MusicSpace, ir: [Float32Array, Float32Array], bpm: number): void {
    if (this.space) return;
    const c = this.ctx;
    const conv = c.createConvolver();
    conv.normalize = false;
    const buf = c.createBuffer(2, ir[0].length, c.sampleRate);
    buf.copyToChannel(ir[0] as Float32Array<ArrayBuffer>, 0);
    buf.copyToChannel(ir[1] as Float32Array<ArrayBuffer>, 1);
    conv.buffer = buf;
    const wet = c.createGain();
    wet.gain.value = dbToGain(spec.reverb.wet);
    conv.connect(wet).connect(this.input.music);
    const layers = Object.keys(this.layers) as MusicLayer[];
    if (!MIXER_FAULTS.noReverb) for (const l of layers) this.sends.reverb[l].connect(conv);
    let delay: DelayNode | null = null;
    if (spec.delay) {
      const d = spec.delay;
      const time = (60 / bpm) * d.beats;
      delay = c.createDelay(4);
      delay.delayTime.value = time;
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = d.lowpass;
      const fb = c.createGain();
      fb.gain.value = Math.min(0.9, Math.max(0, d.feedback));
      delay.connect(lp).connect(fb).connect(delay);
      const ret = c.createGain();
      ret.gain.value = dbToGain(d.wet);
      lp.connect(ret).connect(this.input.music);
      if (d.toReverb) {
        const tr = c.createGain();
        tr.gain.value = d.toReverb;
        ret.connect(tr).connect(conv);
      }
      if (!MIXER_FAULTS.noReverb) for (const l of layers) this.sends.echo[l].connect(delay);
      this.echo = { node: delay, beats: d.beats, time };
    }
    this.space = { convolver: conv, delay, send: spec.reverb.send };
  }

  /** The tempo from `at`: the echo's time follows over a quarter second (a glide, not a jump). */
  tempo(bpm: number, at: number): void {
    if (!this.echo) return;
    const time = (60 / bpm) * this.echo.beats;
    if (Math.abs(time - this.echo.time) < 1e-6) return;
    const p = this.echo.node.delayTime;
    p.cancelScheduledValues(at);
    p.setValueAtTime(this.echo.time, at);
    p.linearRampToValueAtTime(time, at + 0.25);
    this.echo.time = time;
  }
}
